import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit, Trash2, Eye, Image, X, Search, Package,
  Filter, Layers, Tag, Zap, FolderOpen,
} from 'lucide-react';
import Base from '../components/Base';
import AuthContext from '../context/AuthContext';
import Swal from 'sweetalert2';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { devLog } from '../utils/devLog';

const API_BASE_URL_FOR_IMAGES = API_BASE_URL;

// ─── Animation variants (matches HomePage) ──────────────────
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariants = {
  hidden: { y: 16, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: 0.4, ease: 'easeOut' } },
};

// ─── Gradient placeholders for collections without images ────
const GRADIENTS = [
  'linear-gradient(135deg, #d45614 0%, #F4ECE5 100%)',
  'linear-gradient(135deg, #2C1810 0%, #C9A882 100%)',
  'linear-gradient(135deg, #a8430e 0%, #d45614 100%)',
  'linear-gradient(135deg, #C9A882 0%, #F4ECE5 100%)',
  'linear-gradient(135deg, #2C1810 0%, #d45614 100%)',
  'linear-gradient(135deg, #d45614 0%, #C9A882 100%)',
];

const CollectionsManager = () => {
  const { authToken, logoutUser, user } = useContext(AuthContext);

  // ─── State ──────────────────────────────────────────────────
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [productSearch, setProductSearch] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    collection_type: 'manual',
    filter_category: '',
    filter_gender: '',
    filter_type: '',
    product_ids: [],
  });
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // ─── Auth Axios (same pattern as HomePage) ─────────────────
  const authAxios = useMemo(() => axios.create({
    baseURL: `${API_BASE_URL}/api`,
    headers: {
      Authorization: `Bearer ${authToken?.access}`,
    },
  }), [authToken?.access]);

  // ─── Derived data ──────────────────────────────────────────
  const stats = useMemo(() => {
    const total = collections.length;
    const active = collections.filter(c => c.is_active !== false).length;
    const manual = collections.filter(c => c.collection_type === 'manual').length;
    const smart = collections.filter(c => c.collection_type === 'smart').length;
    return { total, active, manual, smart };
  }, [collections]);

  const filteredCollections = useMemo(() => {
    let result = collections;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q)
      );
    }
    if (typeFilter !== 'all') {
      result = result.filter(c => c.collection_type === typeFilter);
    }
    return result;
  }, [collections, searchQuery, typeFilter]);

  const hasActiveFilters = searchQuery.trim() || typeFilter !== 'all';

  const categories = useMemo(() => {
    const cats = [...new Set(products.map(p => p.category).filter(Boolean))];
    return cats.sort();
  }, [products]);

  const genders = useMemo(() => {
    const g = [...new Set(products.map(p => p.gender).filter(Boolean))];
    return g.sort();
  }, [products]);

  const productTypes = useMemo(() => {
    const t = [...new Set(products.map(p => p.type || p.product_type).filter(Boolean))];
    return t.sort();
  }, [products]);

  const filteredPickerProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const q = productSearch.toLowerCase();
    return products.filter(p => p.name?.toLowerCase().includes(q));
  }, [products, productSearch]);

  // Products matched by smart filters
  const smartMatchedProducts = useMemo(() => {
    if (formData.collection_type !== 'smart') return [];
    let result = products;
    if (formData.filter_category) {
      result = result.filter(p => p.category === formData.filter_category);
    }
    if (formData.filter_gender) {
      result = result.filter(p => p.gender === formData.filter_gender);
    }
    if (formData.filter_type) {
      result = result.filter(p => (p.type || p.product_type) === formData.filter_type);
    }
    return result;
  }, [products, formData.collection_type, formData.filter_category, formData.filter_gender, formData.filter_type]);

  // ─── Fetch data ────────────────────────────────────────────
  const fetchCollections = useCallback(async () => {
    try {
      const response = await authAxios.get('/collections/');
      if (response.data && Array.isArray(response.data.collections)) {
        setCollections(response.data.collections);
      } else if (Array.isArray(response.data)) {
        setCollections(response.data);
      } else {
        setCollections([]);
      }
    } catch (err) {
      devLog.error('Failed to fetch collections:', err);
      if (err.response?.status === 401) {
        Swal.fire({
          title: 'Session Expired',
          text: 'Please login again',
          icon: 'warning',
          confirmButtonColor: '#d45614',
        }).then(() => {
          logoutUser();
        });
      }
    }
  }, [authAxios, logoutUser]);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await authAxios.get('/products/');
      if (response.data && Array.isArray(response.data.products)) {
        setProducts(response.data.products);
      } else if (Array.isArray(response.data)) {
        setProducts(response.data);
      } else {
        setProducts([]);
      }
    } catch (err) {
      devLog.error('Failed to fetch products:', err);
    }
  }, [authAxios]);

  useEffect(() => {
    if (authToken) {
      setLoading(true);
      Promise.all([fetchCollections(), fetchProducts()]).finally(() => {
        setLoading(false);
      });
    }
  }, [authToken, fetchCollections, fetchProducts]);

  // ─── Modal helpers ─────────────────────────────────────────
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      collection_type: 'manual',
      filter_category: '',
      filter_gender: '',
      filter_type: '',
      product_ids: [],
    });
    setCoverImage(null);
    setCoverPreview(null);
    setEditingCollection(null);
    setProductSearch('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (collection) => {
    setEditingCollection(collection);
    setFormData({
      name: collection.name || '',
      description: collection.description || '',
      collection_type: collection.collection_type || 'manual',
      filter_category: collection.filter_category || '',
      filter_gender: collection.filter_gender || '',
      filter_type: collection.filter_type || '',
      product_ids: collection.product_ids || (collection.products || []).map(p => typeof p === 'object' ? p.id : p),
    });
    setCoverPreview(collection.cover_image || collection.image || null);
    setCoverImage(null);
    setProductSearch('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // ─── Image handling ────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({ title: 'File Too Large', text: 'Image must be under 5MB.', icon: 'warning', confirmButtonColor: '#d45614' });
        return;
      }
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => setCoverPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  // ─── Product toggle for manual collections ─────────────────
  const toggleProductSelection = (productId) => {
    setFormData(prev => {
      const ids = new Set(prev.product_ids);
      if (ids.has(productId)) {
        ids.delete(productId);
      } else {
        ids.add(productId);
      }
      return { ...prev, product_ids: [...ids] };
    });
  };

  // ─── Save collection ──────────────────────────────────────
  const handleSave = async () => {
    if (!formData.name.trim()) {
      Swal.fire({ title: 'Name Required', text: 'Please enter a collection name.', icon: 'warning', confirmButtonColor: '#d45614' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        collection_type: formData.collection_type,
      };

      if (formData.collection_type === 'manual') {
        payload.product_ids = formData.product_ids;
      } else {
        payload.filter_category = formData.filter_category;
        payload.filter_gender = formData.filter_gender;
        payload.filter_type = formData.filter_type;
      }

      let savedCollection;
      if (editingCollection) {
        const res = await authAxios.put(`/collections/${editingCollection.id}/update/`, payload);
        savedCollection = res.data.collection || res.data;
      } else {
        const res = await authAxios.post('/collections/create/', payload);
        savedCollection = res.data.collection || res.data;
      }

      // Upload image if provided
      if (coverImage && savedCollection?.id) {
        const imageFormData = new FormData();
        imageFormData.append('image', coverImage);
        await authAxios.post(`/collections/${savedCollection.id}/upload-image/`, imageFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
      Toast.fire({ icon: 'success', title: editingCollection ? 'Collection updated!' : 'Collection created!' });

      closeModal();
      fetchCollections();
    } catch (err) {
      devLog.error('Failed to save collection:', err);
      const msg = err.response?.data?.error || err.response?.data?.detail || 'Failed to save collection.';
      Swal.fire({ title: 'Error', text: msg, icon: 'error', confirmButtonColor: '#d45614' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete collection ─────────────────────────────────────
  const handleDelete = (collection) => {
    Swal.fire({
      title: 'Delete this collection?',
      text: `"${collection.name}" will be permanently removed. Products won't be deleted.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, delete',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await authAxios.delete(`/collections/${collection.id}/delete/`);
          const Toast = Swal.mixin({ toast: true, position: 'top-end', showConfirmButton: false, timer: 2000, timerProgressBar: true });
          Toast.fire({ icon: 'success', title: 'Collection deleted!' });
          setCollections(prev => prev.filter(c => c.id !== collection.id));
        } catch (err) {
          devLog.error('Failed to delete collection:', err);
          Swal.fire({ title: 'Error', text: 'Failed to delete collection.', icon: 'error', confirmButtonColor: '#d45614' });
        }
      }
    });
  };

  // ─── Product image helper ──────────────────────────────────
  const getProductImage = (product) => {
    const img = product.img1 || product.img2 || product.img3;
    if (!img) return null;
    if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('/media/')) {
      return img;
    }
    return `${API_BASE_URL_FOR_IMAGES}${img}`;
  };

  // ─── Collection cover image helper ─────────────────────────
  const getCollectionImage = (collection) => {
    const img = collection.cover_image || collection.image;
    if (!img) return null;
    if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('/media/')) {
      return img;
    }
    return `${API_BASE_URL_FOR_IMAGES}${img}`;
  };

  // ─── Store URL ─────────────────────────────────────────────
  const storeUrl = user?.username ? `https://${user.username}.vasstr.com` : '#';

  // ─── Loading State ─────────────────────────────────────────
  if (loading) {
    return (
      <Base Content={
        <div className="flex flex-col items-center justify-center h-screen bg-white gap-3">
          <div className="w-10 h-10 border-3 border-[#d45614] border-t-transparent rounded-full animate-spin" />
          <p className="text-base text-gray-500 font-fell">Loading collections...</p>
        </div>
      } />
    );
  }

  // ═══════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════
  return (
    <Base
      Content={
        <div className="min-h-screen bg-white">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-8"
          >
            {/* ─── Header Card ──────────────────────────────── */}
            <motion.div
              variants={itemVariants}
              className="bg-[#F4ECE5] rounded-[9px] border border-[#C9A882]/30 p-5 sm:p-7 mb-6 shadow-sm"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-[#d45614] flex items-center justify-center shadow-sm">
                      <Layers className="w-5 h-5 text-white" />
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#2C1810] font-fell">
                      Collections
                    </h1>
                  </div>
                  <p className="text-sm text-gray-500 ml-[52px]">
                    Organize your products into collections for your store.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={openCreateModal}
                    className="bg-[#d45614] text-white text-sm font-medium px-5 py-2.5 rounded-[9px] transition-all shadow-sm hover:bg-[#a8430e] hover:shadow-md inline-flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Create Collection
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* ─── Stats Cards ───────────────────────────────── */}
            <motion.div
              variants={containerVariants}
              className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6"
            >
              {[
                { label: 'Total Collections', value: stats.total, sub: 'in your store', icon: <Layers className="w-5 h-5 sm:w-6 sm:h-6" />, color: '#d45614', bgColor: '#FEF3ED' },
                { label: 'Active', value: stats.active, sub: 'visible to customers', icon: <Eye className="w-5 h-5 sm:w-6 sm:h-6" />, color: '#10B981', bgColor: '#D1FAE5' },
                { label: 'Manual', value: stats.manual, sub: 'hand-picked products', icon: <Tag className="w-5 h-5 sm:w-6 sm:h-6" />, color: '#6366F1', bgColor: '#E0E7FF' },
                { label: 'Smart', value: stats.smart, sub: 'auto-filtered products', icon: <Zap className="w-5 h-5 sm:w-6 sm:h-6" />, color: '#F59E0B', bgColor: '#FEF3C7' },
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  variants={itemVariants}
                  whileHover={{ y: -3, transition: { duration: 0.2 } }}
                  className="bg-white rounded-[9px] border border-gray-200 p-4 sm:p-5 shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">{stat.label}</p>
                    <div
                      className="w-8 h-8 sm:w-9 sm:h-9 rounded-[9px] flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: stat.bgColor, color: stat.color }}
                    >
                      {stat.icon}
                    </div>
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-[#2C1810] font-fell">{stat.value}</p>
                  <p className="text-xs text-gray-400 mt-1">{stat.sub}</p>
                </motion.div>
              ))}
            </motion.div>

            {/* ─── Filter Bar ────────────────────────────────── */}
            <motion.div variants={itemVariants} className="mb-5">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                {/* Search */}
                <div className="relative flex-1 min-w-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search collections..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-[9px] focus:outline-none focus:border-[#d45614] focus:ring-1 focus:ring-[#d45614]/20 bg-white"
                  />
                  {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                      <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
                {/* Type filter */}
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="text-sm border border-gray-200 rounded-[9px] px-3 py-2 bg-white focus:outline-none focus:border-[#d45614] w-full sm:w-auto sm:min-w-[140px]"
                >
                  <option value="all">All Types</option>
                  <option value="manual">Manual</option>
                  <option value="smart">Smart</option>
                </select>
                {/* Clear Filters */}
                {hasActiveFilters && (
                  <button
                    onClick={() => { setSearchQuery(''); setTypeFilter('all'); }}
                    className="flex items-center gap-1 text-xs font-medium text-[#d45614] hover:text-[#a8430e] px-2 py-2 whitespace-nowrap"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear
                  </button>
                )}
              </div>
            </motion.div>

            {/* ─── No Results ────────────────────────────────── */}
            {hasActiveFilters && filteredCollections.length === 0 && (
              <motion.div variants={itemVariants} className="text-center py-12 mb-6">
                <Filter className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-500 font-medium">No collections match your filters</p>
                <button onClick={() => { setSearchQuery(''); setTypeFilter('all'); }} className="text-xs text-[#d45614] hover:text-[#a8430e] font-medium mt-2">
                  Clear all filters
                </button>
              </motion.div>
            )}

            {/* ─── Empty State ───────────────────────────────── */}
            {!hasActiveFilters && collections.length === 0 && (
              <motion.div variants={itemVariants} className="text-center py-16">
                <motion.div
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-20 h-20 rounded-full bg-[#F4ECE5] flex items-center justify-center mx-auto mb-6 shadow-md border border-[#C9A882]/20"
                >
                  <FolderOpen className="w-10 h-10 text-[#d45614]" />
                </motion.div>
                <h2 className="text-2xl font-bold text-[#2C1810] font-fell mb-3">No Collections Yet</h2>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Collections help you organize products and make it easier for customers to browse your store.
                </p>
                <motion.button
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={openCreateModal}
                  className="bg-[#d45614] text-white font-semibold px-8 py-3 rounded-[9px] text-base shadow-lg hover:bg-[#a8430e] hover:shadow-xl transition-all duration-200 inline-flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Create Your First Collection
                </motion.button>
              </motion.div>
            )}

            {/* ─── Collections Grid ──────────────────────────── */}
            {filteredCollections.length > 0 && (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-5 mb-10"
              >
                {/* Add New Card */}
                <motion.div variants={itemVariants}>
                  <motion.div
                    whileHover={{ y: -4, transition: { duration: 0.2 } }}
                    onClick={openCreateModal}
                    className="cursor-pointer border-2 border-dashed border-[#C9A882]/50 rounded-[9px] overflow-hidden hover:border-[#d45614]/40 transition-all duration-200 bg-[#F4ECE5]/30 h-full min-h-[280px]"
                  >
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center">
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="w-14 h-14 rounded-full bg-[#d45614] flex items-center justify-center mx-auto mb-3 shadow-md"
                        >
                          <Plus className="w-7 h-7 text-white" />
                        </motion.div>
                        <p className="text-sm font-semibold text-[#2C1810] font-fell">
                          New Collection
                        </p>
                        <p className="text-xs text-gray-400 mt-1">Add a new collection</p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>

                {/* Collection Cards */}
                {filteredCollections.map((collection, index) => {
                  const collectionImage = getCollectionImage(collection);
                  const productCount = collection.product_count ?? collection.products?.length ?? 0;
                  const gradient = GRADIENTS[index % GRADIENTS.length];

                  return (
                    <motion.div
                      key={collection.id}
                      variants={itemVariants}
                      whileHover={{ y: -4, transition: { duration: 0.2 } }}
                      className="group bg-white rounded-[9px] border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      {/* Cover Image */}
                      <div className="relative aspect-[16/9] overflow-hidden bg-gray-100">
                        {collectionImage ? (
                          <img
                            src={collectionImage}
                            alt={collection.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center"
                            style={{ background: gradient }}
                          >
                            <Layers className="w-10 h-10 text-white/60" />
                          </div>
                        )}
                        {/* Type badge */}
                        <div className="absolute top-3 left-3">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full backdrop-blur-sm ${
                            collection.collection_type === 'smart'
                              ? 'bg-amber-100/90 text-amber-700'
                              : 'bg-indigo-100/90 text-indigo-700'
                          }`}>
                            {collection.collection_type === 'smart' ? (
                              <span className="inline-flex items-center gap-1"><Zap className="w-3 h-3" /> Smart</span>
                            ) : (
                              <span className="inline-flex items-center gap-1"><Tag className="w-3 h-3" /> Manual</span>
                            )}
                          </span>
                        </div>
                        {/* Hover overlay actions */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal(collection)}
                              className="w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors shadow-sm"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4 text-[#2C1810]" />
                            </button>
                            <button
                              onClick={() => handleDelete(collection)}
                              className="w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors shadow-sm"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </button>
                            {user?.username && (
                              <a
                                href={`${storeUrl}/collections/${collection.slug || collection.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-9 h-9 rounded-full bg-white/90 hover:bg-white flex items-center justify-center transition-colors shadow-sm"
                                title="View on store"
                              >
                                <Eye className="w-4 h-4 text-[#d45614]" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card content */}
                      <div className="p-4">
                        <h3 className="text-base font-semibold text-[#2C1810] font-fell truncate mb-1">
                          {collection.name}
                        </h3>
                        {collection.description && (
                          <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                            {collection.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-400 inline-flex items-center gap-1">
                            <Package className="w-3.5 h-3.5" />
                            {productCount} {productCount === 1 ? 'product' : 'products'}
                          </span>
                          <button
                            onClick={() => openEditModal(collection)}
                            className="text-xs font-medium text-[#d45614] hover:text-[#a8430e] transition-colors"
                          >
                            Edit
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </motion.div>

          {/* ═══════════════════════════════════════════════════ */}
          {/* CREATE / EDIT MODAL                                */}
          {/* ═══════════════════════════════════════════════════ */}
          <AnimatePresence>
            {showModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto"
                style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
                onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
              >
                <motion.div
                  initial={{ opacity: 0, y: 40, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 40, scale: 0.97 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className="bg-white rounded-[9px] shadow-2xl w-full max-w-3xl my-8 mx-4 overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Modal Header */}
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-[#F4ECE5]">
                    <h2 className="text-lg font-bold text-[#2C1810] font-fell">
                      {editingCollection ? 'Edit Collection' : 'Create Collection'}
                    </h2>
                    <button
                      onClick={closeModal}
                      className="w-8 h-8 rounded-full hover:bg-[#C9A882]/30 flex items-center justify-center transition-colors"
                    >
                      <X className="w-5 h-5 text-[#2C1810]" />
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="px-6 py-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {/* Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-[#2C1810] mb-1.5">
                        Collection Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g. Summer Collection 2026"
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-[9px] focus:outline-none focus:border-[#d45614] focus:ring-1 focus:ring-[#d45614]/20 bg-white"
                      />
                    </div>

                    {/* Description */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-[#2C1810] mb-1.5">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Describe this collection..."
                        rows={3}
                        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-[9px] focus:outline-none focus:border-[#d45614] focus:ring-1 focus:ring-[#d45614]/20 bg-white resize-none"
                      />
                    </div>

                    {/* Cover Image */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-[#2C1810] mb-1.5">
                        Cover Image
                      </label>
                      <div className="flex items-start gap-4">
                        {/* Preview */}
                        <div className="w-32 h-20 rounded-[9px] overflow-hidden border border-gray-200 bg-gray-50 flex-shrink-0 flex items-center justify-center">
                          {coverPreview ? (
                            <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" />
                          ) : (
                            <Image className="w-6 h-6 text-gray-300" />
                          )}
                        </div>
                        <div className="flex-1">
                          <label className="cursor-pointer inline-flex items-center gap-2 text-sm font-medium text-[#d45614] hover:text-[#a8430e] transition-colors px-4 py-2 border border-[#d45614]/30 rounded-[9px] hover:bg-[#d45614]/5">
                            <Image className="w-4 h-4" />
                            {coverPreview ? 'Change Image' : 'Upload Image'}
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageChange}
                              className="hidden"
                            />
                          </label>
                          <p className="text-xs text-gray-400 mt-1.5">JPG, PNG. Max 5MB.</p>
                          {coverPreview && (
                            <button
                              onClick={() => { setCoverImage(null); setCoverPreview(null); }}
                              className="text-xs text-red-500 hover:text-red-600 mt-1"
                            >
                              Remove image
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Collection Type Toggle */}
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-[#2C1810] mb-2">
                        Collection Type
                      </label>
                      <div className="flex rounded-[9px] border border-gray-200 overflow-hidden">
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, collection_type: 'manual', product_ids: prev.product_ids }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-all ${
                            formData.collection_type === 'manual'
                              ? 'bg-[#d45614] text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <Tag className="w-4 h-4" />
                          Manual
                        </button>
                        <button
                          onClick={() => setFormData(prev => ({ ...prev, collection_type: 'smart' }))}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium transition-all ${
                            formData.collection_type === 'smart'
                              ? 'bg-[#d45614] text-white'
                              : 'bg-white text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <Zap className="w-4 h-4" />
                          Smart
                        </button>
                      </div>
                    </div>

                    {/* ── Smart Collection Filters ──────────────── */}
                    {formData.collection_type === 'smart' && (
                      <div className="mb-5 p-4 bg-amber-50/50 border border-amber-200/50 rounded-[9px]">
                        <p className="text-xs font-semibold text-amber-700 mb-3 inline-flex items-center gap-1">
                          <Zap className="w-3.5 h-3.5" />
                          Smart filters auto-add matching products
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                            <select
                              value={formData.filter_category}
                              onChange={(e) => setFormData(prev => ({ ...prev, filter_category: e.target.value }))}
                              className="w-full text-sm border border-gray-200 rounded-[9px] px-3 py-2 bg-white focus:outline-none focus:border-[#d45614]"
                            >
                              <option value="">Any Category</option>
                              {categories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Gender</label>
                            <select
                              value={formData.filter_gender}
                              onChange={(e) => setFormData(prev => ({ ...prev, filter_gender: e.target.value }))}
                              className="w-full text-sm border border-gray-200 rounded-[9px] px-3 py-2 bg-white focus:outline-none focus:border-[#d45614]"
                            >
                              <option value="">Any Gender</option>
                              {genders.map(g => (
                                <option key={g} value={g}>{g}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                            <select
                              value={formData.filter_type}
                              onChange={(e) => setFormData(prev => ({ ...prev, filter_type: e.target.value }))}
                              className="w-full text-sm border border-gray-200 rounded-[9px] px-3 py-2 bg-white focus:outline-none focus:border-[#d45614]"
                            >
                              <option value="">Any Type</option>
                              {productTypes.map(t => (
                                <option key={t} value={t}>{t}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        {/* Smart match preview */}
                        <div className="mt-3 pt-3 border-t border-amber-200/50">
                          <p className="text-xs text-amber-700">
                            <strong>{smartMatchedProducts.length}</strong> product{smartMatchedProducts.length !== 1 ? 's' : ''} match{smartMatchedProducts.length === 1 ? 'es' : ''} these filters
                          </p>
                          {smartMatchedProducts.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {smartMatchedProducts.slice(0, 8).map(p => (
                                <div key={p.id} className="flex items-center gap-1.5 bg-white border border-amber-200 rounded-full px-2 py-0.5">
                                  {getProductImage(p) && (
                                    <img src={getProductImage(p)} alt="" className="w-4 h-4 rounded-full object-cover" />
                                  )}
                                  <span className="text-xs text-gray-700 truncate max-w-[100px]">{p.name}</span>
                                </div>
                              ))}
                              {smartMatchedProducts.length > 8 && (
                                <span className="text-xs text-amber-600 px-2 py-0.5">
                                  +{smartMatchedProducts.length - 8} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── Manual Product Picker ─────────────────── */}
                    {formData.collection_type === 'manual' && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-[#2C1810]">
                            Select Products
                          </label>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full font-medium">
                            {formData.product_ids.length} selected
                          </span>
                        </div>

                        {/* Product search */}
                        <div className="relative mb-3">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <input
                            type="text"
                            placeholder="Search products..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-[9px] focus:outline-none focus:border-[#d45614] focus:ring-1 focus:ring-[#d45614]/20 bg-white"
                          />
                          {productSearch && (
                            <button onClick={() => setProductSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </button>
                          )}
                        </div>

                        {/* Product grid */}
                        <div className="border border-gray-200 rounded-[9px] max-h-[300px] overflow-y-auto custom-scrollbar">
                          {products.length === 0 ? (
                            <div className="p-8 text-center">
                              <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">No products found. Upload products first.</p>
                            </div>
                          ) : filteredPickerProducts.length === 0 ? (
                            <div className="p-8 text-center">
                              <Search className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                              <p className="text-sm text-gray-500">No products match your search.</p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 p-3">
                              {filteredPickerProducts.map(product => {
                                const isSelected = formData.product_ids.includes(product.id);
                                const productImg = getProductImage(product);
                                return (
                                  <div
                                    key={product.id}
                                    onClick={() => toggleProductSelection(product.id)}
                                    className={`cursor-pointer rounded-[9px] border-2 overflow-hidden transition-all duration-150 ${
                                      isSelected
                                        ? 'border-[#d45614] ring-1 ring-[#d45614]/30 shadow-sm'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="aspect-square bg-gray-50 overflow-hidden relative">
                                      {productImg ? (
                                        <img
                                          src={productImg}
                                          alt={product.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                          <Package className="w-6 h-6 text-gray-300" />
                                        </div>
                                      )}
                                      {/* Checkbox overlay */}
                                      <div className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center transition-all ${
                                        isSelected
                                          ? 'bg-[#d45614] text-white'
                                          : 'bg-white/80 border border-gray-300'
                                      }`}>
                                        {isSelected && (
                                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                          </svg>
                                        )}
                                      </div>
                                    </div>
                                    <div className="px-1.5 py-1.5">
                                      <p className="text-xs text-[#2C1810] truncate font-medium">
                                        {product.name}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Modal Footer */}
                  <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <button
                      onClick={closeModal}
                      className="text-sm font-medium text-gray-600 px-5 py-2.5 rounded-[9px] border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      Cancel
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleSave}
                      disabled={saving}
                      className={`text-sm font-medium text-white px-6 py-2.5 rounded-[9px] shadow-sm transition-all inline-flex items-center gap-2 ${
                        saving
                          ? 'bg-[#d45614]/60 cursor-not-allowed'
                          : 'bg-[#d45614] hover:bg-[#a8430e] hover:shadow-md'
                      }`}
                    >
                      {saving && (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      )}
                      {saving ? 'Saving...' : (editingCollection ? 'Update Collection' : 'Create Collection')}
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      }
    />
  );
};

export default CollectionsManager;
